import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../services/setupInterceptors";

const initialState = {
  addresses: [],
  status: "idle",
  error: null,
};

export const fetchUserAddresses = createAsyncThunk(
  "user/fetchAddresses",
  async (_, thunkAPI) => {
    try {
      const { data } = await axiosClient.get("/users/address/all");
      return data?.success ? data.data : thunkAPI.rejectWithValue("Error fetching addresses");
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Error fetching addresses");
    }
  },
  {
    condition: (_, { getState }) => {
      return getState().userAddress.addresses.length === 0;
    },
  }
);

export const addUserAddress = createAsyncThunk(
  "user/addAddress",
  async (address, thunkAPI) => {
    try {
      const { data } = await axiosClient.post("/users/address", address);
      return data?.success ? data.data : thunkAPI.rejectWithValue("Error adding address");
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Error adding address");
    }
  }
);

export const editUserAddress = createAsyncThunk(
  "user/editAddress",
  async ({ id, address }, thunkAPI) => {
    try {
      const { data } = await axiosClient.patch(`/users/address/${id}`, address);
      return data?.success ? data.data : thunkAPI.rejectWithValue("Error editing address");
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Error editing address");
    }
  }
);

export const makeDefaultAddress = createAsyncThunk(
  "user/makeDefaultAddress",
  async (id, thunkAPI) => {
    try {
      const { data } = await axiosClient.patch(`/users/address/${id}/default`);
      return data?.success ? id : thunkAPI.rejectWithValue("Error setting default address");
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Error setting default address");
    }
  }
);

export const deleteUserAddress = createAsyncThunk(
  "user/deleteAddress",
  async (id, thunkAPI) => {
    try {
      const { data } = await axiosClient.delete(`/users/address/${id}`);
      return data?.success ? id : thunkAPI.rejectWithValue("Error deleting address");
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Error deleting address");
    }
  }
);

const userAddressSlice = createSlice({
  name: "userAddress",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserAddresses.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchUserAddresses.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.addresses = action.payload;
      })
      .addCase(fetchUserAddresses.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      
      .addCase(addUserAddress.fulfilled, (state, action) => {
        state.addresses.push(action.payload);
      })
      .addCase(addUserAddress.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(editUserAddress.fulfilled, (state, action) => {
        const index = state.addresses.findIndex(addr => addr.id === action.payload.id);
        if (index !== -1) {
          state.addresses[index] = action.payload;
        }
      })
      .addCase(editUserAddress.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(makeDefaultAddress.fulfilled, (state, action) => {
        state.addresses = state.addresses.map(addr => ({
          ...addr,
          is_default: addr.id === action.payload,
        }));
      })
      .addCase(makeDefaultAddress.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteUserAddress.fulfilled, (state, action) => {
        state.addresses = state.addresses.filter(addr => addr.id !== action.payload);
      })
      .addCase(deleteUserAddress.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default userAddressSlice.reducer;
